import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { getSession, useGetAllProfileQuery, useGetPublicOnlineUsersQuery } from '@onesource/services';
import { useMemo, useState } from 'react';
import { IoSearch } from 'react-icons/io5';
import { match } from 'ts-pattern';

import { WhoIsLoading } from './loading';
import { NoOneOnline } from './no-one-online';
import { NoSearchResults } from './no-search-results';
import { SearchBox } from './styled';
import { UserListItem } from './user-list-item';

const REFRESH_INTERVAL_MS = 30 * 1000;

interface WhosOnlineProps {
  /** Card height — defaults to filling the viewport; pass a smaller value when stacked with other widgets. */
  height?: string | number;
}

export default function WhosOnline({ height = 'calc(100vh - 120px)' }: WhosOnlineProps) {
  const [search, setSearch] = useState('');

  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const currentUserId = profile?.id?.toString();

  const { data, isLoading } = useGetPublicOnlineUsersQuery({
    enabled: !!token,
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const allUsers = useMemo(() => {
    const fetched = data?.users ?? [];

    const sorted = fetched
      .filter(u => !u.isPrivate && u.id !== currentUserId)
      .sort((a, b) => {
        if (a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1;
        }

        return b.timestamp - a.timestamp;
      });

    const set = new Set<string>();
    return sorted.filter(u => {
      if (set.has(u.id)) {
        return false;
      }
      set.add(u.id);
      return true;
    });
  }, [data?.users, currentUserId]);

  const users = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return allUsers;
    }
    return allUsers.filter(u => u.name?.toLowerCase().includes(query));
  }, [allUsers, search]);

  return (
    <Box
      sx={{
        height,
        flexShrink: 0,
        width: { xs: '100%', md: 265 },
        bgcolor: '#fff',
        borderRadius: 4,
        boxShadow: '0 8px 10px rgba(0,0,0,0.04)',
        px: 2.8,
        pt: 2.5,
        pb: 1.5,
        fontFamily: "'DM Sans', sans-serif",
        overflowY: 'auto',
        // Column flex so the list (or an empty state) takes the space left
        // under the search box and title instead of a fixed offset.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SearchBox sx={{ flexShrink: 0 }}>
        <InputBase
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search"
          sx={{ fontSize: 14, color: '#888', flex: 1, fontFamily: 'inherit' }}
        />
        <IoSearch size={16} color="#888" />
      </SearchBox>

      <Typography
        sx={{
          fontFamily: "'Figtree', sans-serif",
          fontWeight: 700,
          fontSize: { xs: 14, md: 14 },
          color: '#1a1a2e',
          letterSpacing: '-0.2px',
          mb: 1,
          flexShrink: 0,
        }}
      >
        Who&apos;s Online
      </Typography>

      {match({ isLoading, allUsersCount: allUsers.length, usersCount: users.length })
        .with({ isLoading: true }, () => <WhoIsLoading />)
        .with({ allUsersCount: 0 }, () => <NoOneOnline />)
        .with({ usersCount: 0 }, () => <NoSearchResults />)
        .otherwise(() => (
          <List
            disablePadding
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarColor: '#e6e6e7 transparent',
              scrollbarWidth: 'thin',
            }}
          >
            {users.map((member, index) => (
              <Box key={`${member.id}:${member.app}`}>
                <UserListItem member={member} />
                {index < users.length - 1 && <Divider sx={{ borderColor: '#f0f0f4' }} />}
              </Box>
            ))}
          </List>
        ))}
    </Box>
  );
}
